import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuthStore } from '@/stores/useAuthStore';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import { signupSchema } from '@/lib/validations';

export default function Signup() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { signup, isAuthenticated } = useAuthStore();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [language, setLanguage] = useState<'pt-BR' | 'en' | 'es'>('pt-BR');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate inputs
      const validation = signupSchema.safeParse({ 
        nome, 
        email, 
        password, 
        confirmPassword 
      });
      
      if (!validation.success) {
        const firstError = validation.error.issues[0];
        toast.error(firstError.message);
        setIsLoading(false);
        return;
      }

      const { success, error } = await signup(nome, email, password, language);
      
      if (success) {
        toast.success(t('signup.success'));
        navigate('/onboarding/intro');
      } else {
        toast.error(error || t('signup.errors.signupFailed'));
      }
    } catch (error) {
      toast.error('Erro ao criar conta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 gradient-hero">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl gradient-hero shadow-glow">
              <UserPlus className="h-7 w-7 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">{t('signup.title')}</CardTitle>
          <CardDescription className="text-center">
            {t('signup.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">{t('signup.name')}</Label>
              <Input
                id="nome"
                type="text"
                placeholder={t('signup.name')}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('signup.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('signup.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('signup.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('signup.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="language">{t('signup.language')}</Label>
              <Select value={language} onValueChange={(val) => setLanguage(val as 'pt-BR' | 'en' | 'es')}>
                <SelectTrigger>
                  <SelectValue placeholder={t('signup.selectLanguage')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pt-BR">🇧🇷 {t('languages.pt-BR')}</SelectItem>
                  <SelectItem value="en">🇺🇸 {t('languages.en')}</SelectItem>
                  <SelectItem value="es">🇪🇸 {t('languages.es')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? `${t('signup.signupButton')}...` : t('signup.signupButton')}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm">
            <span className="text-muted-foreground">{t('signup.hasAccount')} </span>
            <Link to="/auth/login" className="text-primary hover:underline font-medium">
              {t('signup.loginLink')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
